import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertTriangle,
  ArrowLeft,
  Layers,
  MapPin,
  Package,
  Search,
  Wallet,
} from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { useLocation } from '@/contexts/LocationContext';
import { supabase } from '@/integrations/supabase/client';
import {
  calculateDeadStockReport,
  DEAD_STOCK_THRESHOLD_DAYS,
  formatDeadStockActivitySource,
  groupDeadStockByCategory,
  type DeadStockRow,
} from '@/lib/dead-stock';

export default function DeadStockDetail() {
  const navigate = useNavigate();
  const { companyId } = useAuth();
  const { currentLocation, availableLocations, canSwitchLocation, setCurrentLocation } = useLocation();

  const [selectedLocation, setSelectedLocation] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [deadStockItems, setDeadStockItems] = useState<DeadStockRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedLocation(currentLocation?.id || '');
  }, [currentLocation?.id]);

  const effectiveLocationId = selectedLocation || currentLocation?.id || null;

  useEffect(() => {
    const fetchDeadStockData = async () => {
      if (!companyId) return;

      try {
        setLoading(true);
        setError(null);

        const pageSize = 1000;

        // 1) Converted on-hold -> linked sale IDs. Used to ignore 0 TL conversion sales.
        let convertedOnHoldData: Array<{ linked_sale_id: string | null }> = [];
        let convertedOnHoldPage = 0;
        let convertedOnHoldHasMore = true;

        while (convertedOnHoldHasMore) {
          let convertedOnHoldQuery = supabase
            .from('on_holds')
            .select('linked_sale_id')
            .eq('company_id', companyId)
            .eq('status', 'converted')
            .not('linked_sale_id', 'is', null);

          if (effectiveLocationId) {
            convertedOnHoldQuery = convertedOnHoldQuery.eq('location_id', effectiveLocationId);
          }

          const { data: pageData, error: convertedOnHoldError } = await convertedOnHoldQuery
            .range(convertedOnHoldPage * pageSize, (convertedOnHoldPage + 1) * pageSize - 1);

          if (convertedOnHoldError) throw convertedOnHoldError;

          if (pageData && pageData.length > 0) {
            convertedOnHoldData = [...convertedOnHoldData, ...pageData];
            convertedOnHoldPage++;
            convertedOnHoldHasMore = pageData.length === pageSize;
          } else {
            convertedOnHoldHasMore = false;
          }
        }

        const onHoldLinkedSaleIds = new Set(
          convertedOnHoldData
            .map((row) => row.linked_sale_id)
            .filter((value): value is string => Boolean(value)),
        );

        // 2) Products
        let productsData: any[] = [];
        let productsPage = 0;
        let productsHasMore = true;

        while (productsHasMore) {
          let productsQuery = supabase
            .from('products')
            .select('id, name, item_code, category, quantity_on_hand, current_cost, initial_cost, last_sale_at, created_at')
            .eq('company_id', companyId)
            .gt('quantity_on_hand', 0);

          if (effectiveLocationId) {
            productsQuery = productsQuery.eq('location_id', effectiveLocationId);
          }

          const { data: pageData, error: productsError } = await productsQuery
            .range(productsPage * pageSize, (productsPage + 1) * pageSize - 1);

          if (productsError) throw productsError;

          if (pageData && pageData.length > 0) {
            productsData = [...productsData, ...pageData];
            productsPage++;
            productsHasMore = pageData.length === pageSize;
          } else {
            productsHasMore = false;
          }
        }

        // 3) Full on-hold history for active/recent/last-activity checks
        let onHoldHistoryData: any[] = [];
        let onHoldHistoryPage = 0;
        let onHoldHistoryHasMore = true;

        while (onHoldHistoryHasMore) {
          let onHoldHistoryQuery = supabase
            .from('on_holds')
            .select('product_id, date_on_hold, status')
            .eq('company_id', companyId);

          if (effectiveLocationId) {
            onHoldHistoryQuery = onHoldHistoryQuery.eq('location_id', effectiveLocationId);
          }

          const { data: pageData, error: onHoldHistoryError } = await onHoldHistoryQuery
            .range(onHoldHistoryPage * pageSize, (onHoldHistoryPage + 1) * pageSize - 1);

          if (onHoldHistoryError) throw onHoldHistoryError;

          if (pageData && pageData.length > 0) {
            onHoldHistoryData = [...onHoldHistoryData, ...pageData];
            onHoldHistoryPage++;
            onHoldHistoryHasMore = pageData.length === pageSize;
          } else {
            onHoldHistoryHasMore = false;
          }
        }

        const days180Ago = new Date();
        days180Ago.setDate(days180Ago.getDate() - DEAD_STOCK_THRESHOLD_DAYS);
        days180Ago.setHours(0, 0, 0, 0);

        const recentOnHoldData = onHoldHistoryData.filter((row: { date_on_hold?: string | null }) => {
          if (!row?.date_on_hold) return false;
          return new Date(row.date_on_hold) >= days180Ago;
        });

        // 4) Recent sales (last 180 days)
        let recentSalesData: any[] = [];
        let recentSalesPage = 0;
        let recentSalesHasMore = true;

        while (recentSalesHasMore) {
          let recentSalesQuery = supabase
            .from('sales')
            .select('id, total_net, sale_datetime, sale_items(product_id)')
            .eq('company_id', companyId)
            .eq('status', 'completed')
            .gte('sale_datetime', days180Ago.toISOString());

          if (effectiveLocationId) {
            recentSalesQuery = recentSalesQuery.eq('location_id', effectiveLocationId);
          }

          const { data: pageData, error: recentSalesError } = await recentSalesQuery
            .range(recentSalesPage * pageSize, (recentSalesPage + 1) * pageSize - 1);

          if (recentSalesError) throw recentSalesError;

          if (pageData && pageData.length > 0) {
            recentSalesData = [...recentSalesData, ...pageData];
            recentSalesPage++;
            recentSalesHasMore = pageData.length === pageSize;
          } else {
            recentSalesHasMore = false;
          }
        }

        recentSalesData = recentSalesData.filter((sale) => {
          const isZeroAmount = Math.abs(sale.total_net || 0) < 0.01;
          const isLinkedToOnHold = onHoldLinkedSaleIds.has(sale.id);
          return !(isZeroAmount && isLinkedToOnHold);
        });

        // 5) Full movement history for last activity timestamp.
        // Important: query by product ids (not location_id), because legacy/manual movement
        // rows may have null location_id and should still count as activity.
        let movementHistoryData: any[] = [];
        const productIds: string[] = productsData.map((product) => product.id).filter(Boolean);
        const movementProductChunkSize = 200;

        for (let i = 0; i < productIds.length; i += movementProductChunkSize) {
          const productIdChunk = productIds.slice(i, i + movementProductChunkSize);
          let movementHistoryPage = 0;
          let movementHistoryHasMore = true;

          while (movementHistoryHasMore) {
            const { data: pageData, error: movementHistoryError } = await supabase
              .from('inventory_movements')
              .select('product_id, created_at')
              .eq('company_id', companyId)
              .in('product_id', productIdChunk)
              .range(movementHistoryPage * pageSize, (movementHistoryPage + 1) * pageSize - 1);

            if (movementHistoryError) throw movementHistoryError;

            if (pageData && pageData.length > 0) {
              movementHistoryData = [...movementHistoryData, ...pageData];
              movementHistoryPage++;
              movementHistoryHasMore = pageData.length === pageSize;
            } else {
              movementHistoryHasMore = false;
            }
          }
        }

        const deadStockReport = calculateDeadStockReport({
          products: productsData,
          activeOnHoldItems: onHoldHistoryData,
          recentOnHoldItems: recentOnHoldData,
          allOnHoldItems: onHoldHistoryData,
          recentSales: recentSalesData,
          movementHistory: movementHistoryData,
          thresholdDays: DEAD_STOCK_THRESHOLD_DAYS,
        });

        setDeadStockItems(deadStockReport.items);
      } catch (err) {
        console.error('Error fetching dead stock details:', err);
        setError('Ölü stok detayları yüklenirken bir hata oluştu');
        setDeadStockItems([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchDeadStockData();
  }, [companyId, effectiveLocationId]);

  const categories = useMemo(
    () => [...new Set(deadStockItems.map((item) => item.category))].sort((a, b) => a.localeCompare(b, 'tr-TR')),
    [deadStockItems],
  );

  const filteredItems = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return deadStockItems.filter((item) => {
      const categoryMatches = categoryFilter === 'all' || item.category === categoryFilter;
      if (!categoryMatches) return false;
      if (!query) return true;

      return (
        item.name.toLowerCase().includes(query) ||
        item.code.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
      );
    });
  }, [categoryFilter, deadStockItems, searchTerm]);

  const groupedRows = useMemo(() => groupDeadStockByCategory(filteredItems), [filteredItems]);

  const summary = useMemo(() => {
    const totalValue = deadStockItems.reduce((sum, item) => sum + item.value, 0);
    const totalQuantity = deadStockItems.reduce((sum, item) => sum + item.quantity, 0);
    const avgDays = deadStockItems.length
      ? Math.round(deadStockItems.reduce((sum, item) => sum + item.daysInStock, 0) / deadStockItems.length)
      : 0;

    return {
      totalItems: deadStockItems.length,
      totalValue,
      totalQuantity,
      avgDays,
      filteredItems: filteredItems.length,
    };
  }, [deadStockItems, filteredItems.length]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/reports')} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Raporlara Dön
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Ölü Stok Detayları</h1>
              <p className="text-sm text-muted-foreground">
                {DEAD_STOCK_THRESHOLD_DAYS}+ gün hareketsiz kalan tüm ürünler (kategori bazlı detay)
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-red-500" />
                <div>
                  <p className="text-2xl font-bold">{summary.totalItems}</p>
                  <p className="text-xs text-muted-foreground">Ölü Stok Ürün</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <Wallet className="w-6 h-6 text-amber-600" />
                <div>
                  <p className="text-2xl font-bold">₺{summary.totalValue.toLocaleString('tr-TR')}</p>
                  <p className="text-xs text-muted-foreground">Toplam Değer</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <Package className="w-6 h-6 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold">{summary.totalQuantity.toLocaleString('tr-TR')}</p>
                  <p className="text-xs text-muted-foreground">Toplam Adet</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <Layers className="w-6 h-6 text-purple-600" />
                <div>
                  <p className="text-2xl font-bold">{summary.avgDays}</p>
                  <p className="text-xs text-muted-foreground">Ort. Hareketsiz Gün</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Filtreler</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor="deadstock-location">Lokasyon</Label>
                <Select
                  value={selectedLocation || ''}
                  onValueChange={(value) => {
                    setSelectedLocation(value);
                    if (canSwitchLocation) {
                      const nextLocation = availableLocations.find((loc) => loc.id === value);
                      if (nextLocation) {
                        setCurrentLocation(nextLocation);
                      }
                    }
                  }}
                  disabled={!canSwitchLocation}
                >
                  <SelectTrigger id="deadstock-location">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLocations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {location.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="deadstock-category">Kategori</Label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger id="deadstock-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Kategoriler</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="deadstock-search">Arama</Label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="deadstock-search"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="pl-8"
                    placeholder="Ürün adı veya kodu..."
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Ölü stok detayları yükleniyor...
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <p className="font-medium">{error}</p>
            </CardContent>
          </Card>
        ) : groupedRows.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <AlertTriangle className="w-10 h-10 mx-auto mb-3" />
              <p className="font-medium">Filtreye uygun ölü stok bulunamadı</p>
              <p className="text-xs mt-1">
                Görünen kayıt: {summary.filteredItems} / Toplam ölü stok: {summary.totalItems}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {groupedRows.map((group) => (
              <Card key={group.category}>
                <CardHeader>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <CardTitle className="text-lg">{group.category}</CardTitle>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <Badge variant="secondary">{group.itemCount} ürün</Badge>
                      <Badge variant="outline">{group.totalQuantity.toLocaleString('tr-TR')} adet</Badge>
                      <Badge variant="outline">₺{group.totalValue.toLocaleString('tr-TR')}</Badge>
                      <Badge variant="outline">Ort. {group.averageDaysInStock} gün</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ürün</TableHead>
                        <TableHead>Kod</TableHead>
                        <TableHead className="text-right">Miktar</TableHead>
                        <TableHead className="text-right">Değer</TableHead>
                        <TableHead className="text-right">Hareketsiz Gün</TableHead>
                        <TableHead>Son Aktivite</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Link to={`/products/${item.id}`} className="font-medium text-primary hover:underline">
                              {item.name}
                            </Link>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{item.code}</TableCell>
                          <TableCell className="text-right">{item.quantity.toLocaleString('tr-TR')}</TableCell>
                          <TableCell className="text-right">₺{item.value.toLocaleString('tr-TR')}</TableCell>
                          <TableCell className="text-right">
                            <span className="font-semibold text-red-600">{item.daysInStock}</span>
                          </TableCell>
                          <TableCell>
                            {item.lastActivityAt ? (
                              <div className="text-xs">
                                <p className="font-medium">
                                  {new Date(item.lastActivityAt).toLocaleDateString('tr-TR')}
                                </p>
                                <p className="text-muted-foreground">
                                  {formatDeadStockActivitySource(item.lastActivitySource)}
                                </p>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">Bilinmiyor</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
